import { gql } from "@apollo/client/core";
import { graphQlManager } from "./services/server";
import { WorkflowExperience } from "./WorkflowExperience";

const addressValidationResultFragment = gql`
    fragment AddressValidationResultFields on AddressValidationResult {
        addressComplete
        address {
            formattedAddress
            regionCode
            postalCode
            administrativeArea
            locality
            sublocality
        }
        components {
            name
            type
            confirmationLevel
            inferred
            spellCorrected
            replaced
            unexpected
        }
        missingComponentTypes
    }
`;
const addressValidationJobFragment = gql`
    ${addressValidationResultFragment}
    fragment AddressValidationJobFields on AddressValidationJob {
        id
        entityId
        status
        createdAt
        updatedAt
        result {
            ...AddressValidationResultFields
        }
    }
`;

const addressValidationJobsQuery = gql`
    ${addressValidationJobFragment}
    query GetAddressValidationJobs($ids: [String!]!) {
        addressValidationJobs(ids: $ids) {
            ...AddressValidationJobFields
        }
    }
`;

const recipientAddressValidationJobsQuery = gql`
    ${addressValidationJobFragment}
    query GetRecipientsWithAddressValidationJobs($ids: [String!]!) {
        recipients(ids: $ids) {
            id
            addressValidationJob {
                ...AddressValidationJobFields
            }
        }
    }
`;

const recipientValidateMutation = gql`
    ${addressValidationResultFragment}
    mutation ValidateRecipientAddress($id: String!) {
        recipientValidate(id: $id) {
            ...AddressValidationResultFields
        }
    }
`;

const recipientValidateBatchMutation = gql`
    ${addressValidationJobFragment}
    mutation BatchValidateRecipientAddress($ids: [String!]!) {
        recipientValidateBatch(ids: $ids) {
            ...AddressValidationJobFields
        }
    }
`;

export enum AddressValidationResultConfirmationLevel {
    Confirmed = "Confirmed",
    Plausible = "Plausible",
    Unlikely = "Unlikely",
}

export interface AddressValidationResult {
    /**
     * Whether the resulting address is considered complete.
     * An address is considered complete if no components were unexpected or missing.
     */
    addressComplete?: boolean;
    address?: {
        /**
         * The full address, formatted in the standard format for the area.
         */
        formattedAddress?: string;
        /** Country code; AU, US, UK, etc. */
        regionCode?: string;
        postalCode?: string;
        /** The state/province etc. */
        administrativeArea?: string;
        /** Suburb/city */
        locality?: string;
        sublocality?: string;
    };
    /** The individual components of the address, and their associated validation information. */
    components?: AddressComponent[];
    /**
     * Component types that were expected to be on the address, but were not present and could not be inferred by the validator.
     * See https://developers.google.com/maps/documentation/places/web-service/legacy/supported_types#table2 for a list of possible types.
     */
    missingComponentTypes: string[];
}

export interface AddressComponent {
    /** The text value of the component. */
    name: string;
    /**
     * The type of component it is.
     * See https://developers.google.com/maps/documentation/places/web-service/legacy/supported_types#table2 for a list of possible types.
     */
    type: string;
    /** How strongly the validator believes the result to be correct. */
    confirmationLevel?: AddressValidationResultConfirmationLevel;
    /** Whether this component was inferred by the validator, i.e. it was not present on the original input. */
    inferred?: boolean;
    /** Whether this component had spelling corrected by the validator. */
    spellCorrected?: boolean;
    /** Whether this component had its original value completely replaced by the validator. */
    replaced?: boolean;
    /** Whether the validator believes this component to be unexpected for the supplied address. */
    unexpected?: boolean;
}

export enum AddressValidationJobStatus {
    Created = "Created",
    Processing = "Processing",
    Completed = "Completed",
    Failed = "Failed",
    Cancelled = "Cancelled",
}

export interface AddressValidationJob {
    id: string;
    /** The entity this job was created for. */
    entityId?: string;
    /** The current status of this Job. */
    status: AddressValidationJobStatus;
    /** The datetime of when this Job was created. */
    createdAt: string;
    /** The datetime of when this Job was last updated. */
    updatedAt: string;
    /** The validation result. Only present if status is `Confirmed`. */
    result?: AddressValidationResult;
}

/**
 * Fetches Recipients of Workflow Experiences and their associated Address Validation Jobs.
 * The resulting array will be in the same order as the input `ids`. If a Recipient does not have an associated Job, it will be `undefined`.
 * @param workflowExperiences The Workflow Experiences to fetch jobs for.
 * @returns The associated Address Validation Jobs for the requested Workflow Experiences.
 */
export const getValidationJobsForWorkflowExperiences = async (
    workflowExperiences: WorkflowExperience[],
): Promise<(AddressValidationJob | undefined)[] | undefined> => {
    const ids = workflowExperiences
        .map((wfe) => wfe.getWorkflowManager().getTransaction().recipient?.id)
        .filter((it) => !!it);
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        recipients: { id: string; addressValidationJob?: AddressValidationJob }[];
    }>({
        query: recipientAddressValidationJobsQuery,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        variables: {
            ids: ids,
        },
    });
    return workflowExperiences.map((wfe) => {
        const id = wfe.getWorkflowManager().getTransaction().recipient?.id;
        if (!id) {
            return undefined;
        }
        return response.data?.recipients?.find((r) => r.id === id)?.addressValidationJob;
    });
};

/**
 * Retrieves an up-to-date version of the requested Address Validation Jobs.
 * If a Job has been completed, the validation result will be present on the object.
 * @param ids An array of Address Validation Job id strings.
 * @returns An array of Address Validation Jobs.
 */
export const getAddressValidationJobs = async (ids: string[]): Promise<AddressValidationJob[] | undefined> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{
        addressValidationJobs: AddressValidationJob[];
    }>({
        query: addressValidationJobsQuery,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        variables: {
            ids: ids,
        },
    });
    return response.data?.addressValidationJobs;
};

/**
 * Validate a single Workflow Experience's recipient address.
 * @param workflowExperience The Workflow Experience to validate. If this does not have a recipient attached, the function will immediately resolve with `undefined`.
 * @returns The validation result.
 */
export const validateWorkflowExperienceRecipient = async (
    workflowExperience: WorkflowExperience,
): Promise<AddressValidationResult | undefined> => {
    if (!workflowExperience.getWorkflowManager().getTransaction().recipient?.id) {
        return undefined;
    }
    const response = await graphQlManager.getShadowGraphqlClient().mutate<{
        recipientValidate: AddressValidationResult;
    }>({
        mutation: recipientValidateMutation,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        variables: {
            id: workflowExperience.getWorkflowManager().getTransaction().recipient!.id!,
        },
    });
    return response.data?.recipientValidate;
};

/**
 * Requests to validate many Workflow Experiences' recipient addresses asynchronously.
 * @param workflowExperiences The Workflow Experiences to validate.
 * @returns An array of Address Validation Jobs. This is guaranteed to be in the same order as the input array.
 * If an input Workflow Experience does not have a recipient, the associated Job will be `undefined`.
 * The Jobs will likely be incomplete; consider polling `getAddressValidationJobs` with the ids of the Jobs you get from this function.
 */
export const validateWorkflowExperienceRecipients = async (
    workflowExperiences: WorkflowExperience[],
): Promise<(AddressValidationJob | undefined)[] | undefined> => {
    const ids = workflowExperiences
        .map((wfe) => wfe.getWorkflowManager().getTransaction().recipient?.id)
        .filter((it) => !!it);
    const response = await graphQlManager.getShadowGraphqlClient().mutate<{
        recipientValidateBatch: AddressValidationJob[];
    }>({
        mutation: recipientValidateBatchMutation,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        variables: {
            ids: ids,
        },
    });
    return workflowExperiences.map((wfe) => {
        const id = wfe.getWorkflowManager().getTransaction().recipient?.id;
        if (!id) {
            return undefined;
        }
        return response.data?.recipientValidateBatch?.find((r) => r.entityId === id);
    });
};
